import { useEffect, useState, useMemo } from "react";
import QuarryDatabaseService from "../../../services/quarryDatabase.service";
import { FilterQuarryListParms } from "../../../Types/quarryDatabaseTypes";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Tooltip,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import styles from "./QuarryMapView.module.scss";
import { FILTER_MARKER_KEY, quarryDetailsLabels } from "./constants";
import editIcon from "../../../assets/images/edit-document-icon.svg";
import viewIcon from "../../../assets/images/eye-icon.svg";
import emailIcon from "../../../assets/images/email-outline.svg";
import phoneIcon from "../../../assets/images/phone-outline.svg";
import { MdClose } from "react-icons/md";
import clsx from "clsx";
import { Collapse, Typography } from "antd";
import { RightOutlined, DownOutlined } from "@ant-design/icons";
import { getPrevDollorFormattedString } from "../../../Utilities";
import TNLoader from "../../../reusable-components/TNLoader";
import { MapConstants } from "../../../constants/MapConstants";

interface QuarryMapViewProps {
  filter: FilterQuarryListParms;
  handleEditNavigate?: (id: string) => void;
  handleViewNavigate?: (object: object) => void;
  isDrawerOpen?: boolean;
}

const FitBounds = ({ locations, filterLocation }) => {
  const map = useMap();

  useEffect(() => {
    const validLocations = locations?.filter(
      (loc) => loc?.adjustedLat != null && loc?.adjustedLng != null,
    );

    let locationsToFit = [...validLocations];

    if (filterLocation?.latitude && filterLocation?.longitude) {
      locationsToFit.push({
        adjustedLat: filterLocation.latitude,
        adjustedLng: filterLocation.longitude,
      });
    }

    if (locationsToFit && locationsToFit?.length > 0) {
      const bounds = L?.latLngBounds(
        locationsToFit?.map((loc) => [loc?.adjustedLat, loc?.adjustedLng]),
      );

      map?.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, filterLocation, map]);

  return null;
};

const FixMapSize = () => {
  const map = useMap();

  useEffect(() => {
    const timeout = setTimeout(() => {
      map.invalidateSize();
    }, 450); // slightly longer than 0.4s transition

    return () => clearTimeout(timeout);
  }, [map]);

  return null;
};

const createCustomIcon = () => {
  return L.divIcon({
    className: "custom-marker-icon",
    html: `
      <div class="${styles.markerContainer}">
        <span class="${styles.markerStack}">
          <i class="fas fa-map-marker ${styles.customMarkerIconBorder}"></i>
          <i class="fas fa-map-marker-alt ${styles.customMarkerIcon}"></i>
        </span>
      </div>
    `,
    iconSize: [100, 100],
    iconAnchor: [25, 50],
    tooltipAnchor: [0, -25],
  });
};

const createCustomActiveIcon = () => {
  return L.divIcon({
    className: "custom-marker-icon active",
    html: `
      <div class="${styles.activeMarkerContainer}">
        <i class="fas fa-map-marker-alt ${styles.customActiveMarkerIcon}"></i>
      </div>
    `,
    iconSize: [100, 100],
    iconAnchor: [25, 50],
    tooltipAnchor: [0, -25],
  });
};

const createFilterIcon = () => {
  return L.divIcon({
    className: "custom-marker-icon filter",
    html: `
      <div class="${styles.markerContainer}">
        <span class="${styles.markerStack}">
          <i class="fas fa-map-marker ${styles.filterMarkerIconBorder}"></i>
          <i class="fas fa-map-marker-alt ${styles.filterMarkerIcon}"></i>
        </span>
      </div>
    `,
    iconSize: [100, 100],
    iconAnchor: [25, 50],
    tooltipAnchor: [0, -25],
  });
};

const { Panel } = Collapse;
const { Text } = Typography;

const QuarryMapView = (props?: QuarryMapViewProps) => {
  const { filter, handleEditNavigate, handleViewNavigate, isDrawerOpen } =
    props;
  const {
    GOOGLE_SATILLITE_TILE_LAYER_URL,
    GOOGLE_MAP_ATTRIBUTION,
    GOOGLE_TILE_SUBDOMAIN_ARRAY,
  } = MapConstants;
  const { address, ...cleanedFilter } = filter;
  const [quarryId, setQuarryId] = useState<string>("");
  const [isDetailsPaneOpen, setDetailsPanelOpen] = useState<boolean>(false);
  const handleDetailsPanelOpen = () => {
    setActiveMaterialKeys([]);
    setDetailsPanelOpen(true);
  };
  const handleDetailsPanelClose = () => {
    setQuarryId("");
    setActiveMaterialKeys([]);
    setDetailsPanelOpen(false);
  };
  const { FetchQuarryList, FetchQuarryDetails } = QuarryDatabaseService();
  const { data, refetch, isFetching } = FetchQuarryList({
    ...cleanedFilter,
    fromMap: true,
  });

  const {
    data: quarryData,
    refetch: refetchQuarryData,
    isFetching: isQuarryDataFetching,
  } = FetchQuarryDetails(quarryId, true);

  const processedLocations = useMemo(() => {
    if (!data?.data) return [];

    const coordMap = new Map();

    data?.data?.forEach((location) => {
      const { lat, lng } = location;

      if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
        return;
      }

      const coordKey = `${lat},${lng}`;
      if (!coordMap?.has(coordKey)) {
        coordMap?.set(coordKey, []);
      }
      coordMap?.get(coordKey).push(location);
    });

    // Filter marker location
    if (!!filter?.latitude) {
      const filterCoord = `${filter?.latitude},${filter?.longitude}`;
      if (!coordMap?.has(filterCoord)) {
        coordMap?.set(filterCoord, []);
      }
      coordMap?.get(filterCoord).push({
        lat: filter?.latitude,
        lng: filter?.longitude,
        isFilterMarker: true,
      });
    }

    const newLocations = [];
    const BASE_OFFSET = 0.002; //200 - 300 meters gap
    coordMap?.forEach((locations) => {
      const count = locations.length;

      if (count === 1) {
        const loc = locations[0];
        newLocations.push({
          ...loc,
          adjustedLat: loc.lat,
          adjustedLng: loc.lng,
        });
      } else {
        locations.forEach((loc, index) => {
          const angle = (index / count) * (2 * Math.PI);
          const offsetLat = BASE_OFFSET * Math.cos(angle);
          const offsetLng = BASE_OFFSET * Math.sin(angle);

          newLocations.push({
            ...loc,
            adjustedLat: loc.lat + offsetLat,
            adjustedLng: loc.lng + offsetLng,
          });
        });
      }
    });

    return newLocations;
  }, [data?.data, filter?.latitude]);

  const customMarkerIcon = createCustomIcon();
  const activeIcon = createCustomActiveIcon();
  const filterIcon = createFilterIcon();
  const [activeMaterialKeys, setActiveMaterialKeys] = useState<string[]>([]);
  const [activeSubCategoryKeys, setActiveSubCategoryKeys] = useState<string[]>(
    [],
  );
  const handleMaterialCollapseChange = (key: string | string[]) => {
    const newKey = Array.isArray(key) ? key : [key];
    if (newKey.length > 0) {
      setActiveMaterialKeys(newKey);
      const newList = Object?.keys(quarryData?.data?.materials?.[key])?.map(
        (subCategory) => `${key}-${subCategory}`,
      );
      setActiveSubCategoryKeys(newList);
    } else {
      setActiveMaterialKeys([]);
    }
  };

  const handleSubCategoryCollapseChange = (key: string | string[]) => {
    const newKey = Array.isArray(key) ? key : [key];
    setActiveSubCategoryKeys(newKey);
  };
  const expandIcon = ({ isActive }: { isActive: boolean }) =>
    isActive ? (
      <DownOutlined style={{ marginRight: 8 }} />
    ) : (
      <RightOutlined style={{ marginRight: 8 }} />
    );

  useEffect(() => {
    handleDetailsPanelClose();
    refetch();
  }, [filter]);

  useEffect(() => {
    if (quarryId || isDrawerOpen === false) {
      refetchQuarryData();
    }
  }, [quarryId, refetchQuarryData, isDrawerOpen]);

  return (
    <>
      <TNLoader isLoading={isFetching || isQuarryDataFetching} />
      <div className={styles.container}>
        <div className={styles.mapContainer}>
          <MapContainer
            center={[32.1427, -97.3972]} // Default center for Blum
            zoom={4}
            className={styles.map}
            maxBoundsViscosity={1.0}
            minZoom={2}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
            zoomSnap={0.15}
            zoomDelta={0.2}
            wheelPxPerZoomLevel={90}
          >
            <FixMapSize />
            <TileLayer
              attribution={GOOGLE_MAP_ATTRIBUTION}
              url={GOOGLE_SATILLITE_TILE_LAYER_URL}
              subdomains={GOOGLE_TILE_SUBDOMAIN_ARRAY}
              noWrap={true}
            />
            {processedLocations?.map((positions, index) => {
              if (positions?.isFilterMarker)
                return (
                  <Marker
                    key={FILTER_MARKER_KEY}
                    position={[
                      positions?.adjustedLat ?? 0,
                      positions?.adjustedLng ?? 0,
                    ]}
                    icon={filterIcon}
                    zIndexOffset={1000}
                  />
                );
              else
                return (
                  <Marker
                    key={positions?.id || index}
                    position={[
                      positions?.adjustedLat ?? 0,
                      positions?.adjustedLng ?? 0,
                    ]}
                    icon={
                      positions?.id === quarryId ? activeIcon : customMarkerIcon
                    }
                    eventHandlers={{
                      click: () => {
                        setQuarryId(positions?.id);
                        handleDetailsPanelOpen();
                      },
                    }}
                  >
                    <Tooltip
                      direction="top"
                      opacity={1}
                      className={styles.quarryTooltip}
                    >
                      {positions?.quarryName}
                    </Tooltip>
                  </Marker>
                );
            })}
            <FitBounds
              locations={processedLocations}
              filterLocation={
                filter?.latitude && filter?.longitude ? filter : null
              }
            />
          </MapContainer>
        </div>
        <div
          className={clsx(styles.detailsContainer, {
            [styles.hidden]: !isDetailsPaneOpen,
          })}
        >
          <div className={styles.detailHeader}>
            <div className={styles.title}>{quarryData?.data?.quarryName}</div>
            <div className={styles.headerIcons}>
              <div className={styles.actionIcon}>
                <img
                  src={viewIcon}
                  alt="View"
                  onClick={() =>
                    handleViewNavigate?.({
                      ...quarryData?.data,
                      id: quarryId,
                    })
                  }
                />
              </div>
              <div className={styles.actionIcon}>
                <img
                  src={editIcon}
                  alt="Edit"
                  onClick={() => handleEditNavigate(quarryId)}
                />
              </div>
              <div className={styles.actionIcon}>
                <MdClose onClick={handleDetailsPanelClose} />
              </div>
            </div>
          </div>
          <div className={styles.scrollableSection}>
            <div className={styles.quarryDetails}>
              {Object.keys(quarryDetailsLabels)?.map((key) => {
                return (
                  <>
                    <div className={styles.label}>
                      {quarryDetailsLabels[key]}
                    </div>
                    <div className={styles.value}>
                      {quarryData?.data?.[key] || "-"}{" "}
                    </div>
                  </>
                );
              })}
            </div>
            <div className={styles.materialDetails}>
              <div className={styles.label}>Materials & Quantity</div>
              <Collapse
                activeKey={activeMaterialKeys}
                onChange={handleMaterialCollapseChange}
                expandIcon={expandIcon}
                accordion
                ghost
                className={styles.customCollapse}
              >
                {quarryData?.data?.materials &&
                  Object?.keys(quarryData?.data?.materials)?.map(
                    (materialCategory) => (
                      <Panel
                        header={<Text strong>{materialCategory}</Text>}
                        key={materialCategory}
                        className={styles.customPanel}
                      >
                        <Collapse
                          activeKey={activeSubCategoryKeys}
                          onChange={handleSubCategoryCollapseChange}
                          expandIcon={expandIcon}
                          ghost
                          className={styles.customNestedCollapse}
                        >
                          {quarryData?.data?.materials?.[materialCategory] &&
                            Object?.keys(
                              quarryData?.data?.materials?.[materialCategory],
                            )?.map((subCategory) => (
                              <Panel
                                header={<Text>{subCategory}</Text>}
                                key={`${materialCategory}-${subCategory}`}
                                className={styles.customPanel}
                              >
                                <div className={styles.materialList}>
                                  {quarryData?.data?.materials?.[
                                    materialCategory
                                  ]?.[subCategory]?.map((item, idx) => {
                                    return (
                                      <div
                                        className={styles.materialListItem}
                                        key={item?.materialName || idx}
                                      >
                                        {item?.materialName} -{" "}
                                        {`(${getPrevDollorFormattedString(
                                          item?.price,
                                        )} per ${item?.quantityType})`}
                                      </div>
                                    );
                                  })}
                                </div>
                              </Panel>
                            ))}
                        </Collapse>
                      </Panel>
                    ),
                  )}
              </Collapse>
            </div>
            <div className={styles.contatctDetails}>
              <div className={styles.label}>Contact</div>
              {quarryData?.data?.contacts?.map((contact) => {
                return (
                  <div className={styles.contactBlock} key={contact?.id}>
                    <div className={styles.firstBlock}>
                      <div className={styles.value}>{contact?.contactName}</div>
                      <div className={styles.role}>{contact?.contactRole}</div>
                    </div>
                    <div className={styles.secondBlock}>
                      <div className={styles.email}>
                        <img src={emailIcon} alt="Email" />
                        <div>{contact?.email}</div>
                      </div>
                      <div className={styles.phone}>
                        <img src={phoneIcon} alt="Phone" />
                        <div>{contact?.phone}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.notesDetails}>
              <div className={styles.label}>Notes</div>
              <div className={styles.value}>
                {quarryData?.data?.notes || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuarryMapView;
